import { addMonths } from './points-expiry';
import type { BalanceSnapshot, PointsCategory, Program, Redemption } from './schema';
import { addDays } from './time';
import { programInfo } from './valuations';

/**
 * Balances over time. Each wallet entry's logged balances make a step line: a balance holds
 * until the next one, and before the first one the line holds at that first value, so a total
 * doesn't jump the day a program starts being tracked. Totals add up the entries in a wallet
 * tab, in points or in dollars at today's valuations.
 */

export const CATEGORY_LABEL: Record<PointsCategory, string> = {
  cards: 'Credit cards',
  hotels: 'Hotels',
  airlines: 'Airlines',
  other: 'Other',
};

const KIND_CATEGORY = { bank: 'cards', hotel: 'hotels', airline: 'airlines' } as const;

/** The tab a program belongs in, from the valuation table (Rakuten and Hertz are Other). */
export function categoryFor(programId: string): PointsCategory {
  const kind = programInfo(programId)?.kind;
  return kind ? KIND_CATEGORY[kind] : 'other';
}

/** A wallet entry's tab: the one stored on it, or worked out from its program. */
export function programCategory(p: Pick<Program, 'programId' | 'category'>): PointsCategory {
  return p.category ?? categoryFor(p.programId);
}

export type HistoryEntry = Pick<Program, 'id' | 'name' | 'programId' | 'balance' | 'checkedOn'>;

type Snap = Pick<BalanceSnapshot, 'id' | 'entryId' | 'on' | 'at' | 'balance' | 'note' | 'deleted'>;

export interface HistoryPoint {
  on: string;
  balance: number;
  note: string | null;
  /** The snapshot behind it; null for a current balance no snapshot shows yet. */
  snapshotId: string | null;
}

/** Oldest first; within a day, in the order they were logged. */
export const bySnapshotTime = (a: Pick<Snap, 'on' | 'at'>, b: Pick<Snap, 'on' | 'at'>) =>
  a.on.localeCompare(b.on) || a.at - b.at;

/**
 * One entry's balances, oldest first, always ending on its current balance: when the last
 * snapshot doesn't show it, the current balance is added as of the day it was checked.
 */
export function entryHistory(p: HistoryEntry, snaps: Snap[], today: string): HistoryPoint[] {
  const out: HistoryPoint[] = snaps
    .filter((s) => !s.deleted && s.entryId === p.id && s.on <= today)
    .sort(bySnapshotTime)
    .map((s) => ({ on: s.on, balance: s.balance, note: s.note, snapshotId: s.id }));
  const last = out.at(-1);
  if (!last || last.balance !== p.balance) {
    const checked = p.checkedOn && p.checkedOn <= today ? p.checkedOn : today;
    out.push({
      on: last && last.on > checked ? last.on : checked,
      balance: p.balance,
      note: null,
      snapshotId: null,
    });
  }
  return out;
}

/** The balance at the close of a day; before the first known balance, that first balance. */
export function balanceOn(history: HistoryPoint[], day: string): number {
  let v = history[0]?.balance ?? 0;
  for (const h of history) {
    if (h.on > day) break;
    v = h.balance;
  }
  return v;
}

/** The first day any of these entries has a balance for. */
export function earliestDay(histories: Iterable<HistoryPoint[]>): string | null {
  let min: string | null = null;
  for (const h of histories) {
    const d = h[0]?.on;
    if (d && (!min || d < min)) min = d;
  }
  return min;
}

export const HISTORY_RANGES = ['30d', '3m', '6m', '1y', 'all'] as const;
export type HistoryRange = (typeof HISTORY_RANGES)[number];

/** Where the chart starts: the range's start, or the first balance on record if that's later. */
export function rangeStart(range: HistoryRange, today: string, earliest: string | null): string {
  const start =
    range === '30d'
      ? addDays(today, -30)
      : range === '3m'
        ? addMonths(today, -3)
        : range === '6m'
          ? addMonths(today, -6)
          : range === '1y'
            ? addMonths(today, -12)
            : (earliest ?? today);
  const later = earliest && earliest > start ? earliest : start;
  return later > today ? today : later;
}

/** One program's part in a step of the total. */
export interface SeriesMove {
  id: string;
  name: string;
  /** Since the previous point of the series, weighted like the series (points or dollars). */
  change: number;
  /**
   * The program's previous logged balance: the change built up somewhere between then and
   * this day. Null when this is its first one.
   */
  since: string | null;
  /** The snapshots' notes that day. */
  notes: string[];
}

export interface SeriesPoint {
  on: string;
  value: number;
  /** Since the previous point (0 for the first). */
  change: number;
  /** Who moved, biggest change first; programs with only a note that day come last. */
  moves: SeriesMove[];
}

/** Points per entry: 1 for points, cents per point / 100 for dollars; null leaves it out. */
export type Weight = (e: HistoryEntry) => number | null;

/**
 * The total of some entries from one day to another, with a point at the start, at the end and
 * on every day in between that any of them logged a balance.
 */
export function buildSeries(input: {
  entries: HistoryEntry[];
  histories: Map<string, HistoryPoint[]>;
  from: string;
  to: string;
  weight?: Weight;
}): SeriesPoint[] {
  const weight = input.weight ?? (() => 1);
  const included = input.entries
    .map((e) => ({ e, w: weight(e), h: input.histories.get(e.id) ?? [] }))
    .filter((x): x is { e: HistoryEntry; w: number; h: HistoryPoint[] } => x.w != null);
  const days = new Set([input.from, input.to]);
  for (const x of included)
    for (const p of x.h) if (p.on > input.from && p.on < input.to) days.add(p.on);
  const out: SeriesPoint[] = [];
  for (const on of [...days].sort()) {
    const value = included.reduce((n, x) => n + x.w * balanceOn(x.h, on), 0);
    const prev = out.at(-1);
    const moves: SeriesMove[] = [];
    if (prev)
      for (const x of included) {
        const change = x.w * (balanceOn(x.h, on) - balanceOn(x.h, prev.on));
        const notes = x.h.filter((p) => p.on === on && p.note).map((p) => p.note!);
        if (!change && !notes.length) continue;
        const before = x.h.filter((p) => p.on < on).at(-1);
        moves.push({ id: x.e.id, name: x.e.name, change, since: before?.on ?? null, notes });
      }
    moves.sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
    out.push({ on, value, change: prev ? value - prev.value : 0, moves });
  }
  return out;
}

export interface EntryChange {
  start: number;
  now: number;
  change: number;
  /** Change as a share of the starting balance; null from zero. */
  pct: number | null;
  /** Latest of the day it was checked and its newest snapshot. */
  updatedOn: string | null;
}

/** How one entry moved between two days. */
export function entryChange(
  p: HistoryEntry,
  history: HistoryPoint[],
  from: string,
  to: string,
): EntryChange {
  const start = balanceOn(history, from);
  const now = balanceOn(history, to);
  const logged = history.filter((h) => h.snapshotId).at(-1)?.on ?? null;
  const updatedOn = !logged
    ? (p.checkedOn ?? null)
    : !p.checkedOn || logged > p.checkedOn
      ? logged
      : p.checkedOn;
  return { start, now, change: now - start, pct: start ? (now - start) / start : null, updatedOn };
}

const GENERIC = new Set([
  'airways',
  'airlines',
  'club',
  'rewards',
  'membership',
  'privilege',
  'miles',
  'points',
  'honors',
  'one',
  'gold',
  'plus',
  'the',
]);
const words = (name: string) =>
  name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 2 && !GENERIC.has(w));

/**
 * The wallet entry a redemption came out of. Most match by program; where two entries share
 * one (Qatar and British Airways are both Avios) the title has to name it. "Other" ones match
 * only an entry the title names, so a JetBlue flight booked with other points isn't JetBlue's.
 */
export function redemptionEntry<E extends Pick<Program, 'id' | 'programId' | 'name'>>(
  r: Pick<Redemption, 'programId' | 'title'>,
  entries: E[],
): E | undefined {
  const title = r.title.toLowerCase();
  const pool =
    r.programId === 'other'
      ? entries.filter(
          (e) =>
            title.includes(e.name.toLowerCase()) ||
            (e.programId === 'other' && words(e.name).some((w) => title.includes(w))),
        )
      : entries.filter((e) => e.programId === r.programId);
  if (pool.length <= 1) return pool[0];
  const scored = pool
    .map((e) => ({ e, n: words(e.name).filter((w) => title.includes(w)).length }))
    .sort((a, b) => b.n - a.n);
  const [best, next] = scored;
  return best && best.n > 0 && best.n > (next?.n ?? 0) ? best.e : undefined;
}
